import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import FilterOptions from './components/FilterOptions';
import { useNavigate } from 'react-router-dom';
import { tripService, Trip } from '../../api/trips';
import { vehicleService, Vehicle } from '../../api/vehicles';
import { checkinService, Checkin } from '../../api/checkin';
import { getApiErrorMessage } from '../../api';
import {
  formatDatePtBR,
  formatDateTimePtBR,
  formatTimePtBR,
} from '../../utils/formatters';
import {
  ClipboardCheck,
  Search,
  Plus,
  Car,
  MapPin,
  User,
  CalendarDays,
  Clock,
  FileText,
  Eye,
  Play,
  Flag,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  X,
  Gauge,
  Timer,
  Camera,
  Trash2,
  RefreshCw,
  Filter,
} from 'lucide-react';
import {
  PageHeader,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Th,
  Tr,
  Td,
  Input,
  Button,
  Badge,
  Label,
  Select,
  Modal,
  ConfirmModal,
  showToast,
} from '../../components/ui/SharedLayout';

const ITEMS_PER_PAGE = 20;

async function resizeImage(
  base64Str: string,
  maxWidth = 800,
  maxHeight = 800,
  quality = 0.7,
): Promise<string> {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.src = base64Str;
    img.onload = () => {
      let { width, height } = img;
      const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
      width = Math.floor(width * ratio);
      height = Math.floor(height * ratio);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
        return;
      }
      resolve(base64Str);
    };
    img.onerror = () => resolve(base64Str);
  });
}

const SolicitacaoViagem = () => {
  const { user, setPageTitle } = useAuth();
  const navigate = useNavigate();
  const [solicitacoes, setSolicitacoes] = useState<Trip[]>([]);
  const [availableVehicles, setAvailableVehicles] = useState<Vehicle[]>([]);
  const [activeCheckins, setActiveCheckins] = useState<Checkin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string[]>>({});

  const FILTER_CATEGORIES = {
    Status: [
      { id: 'pendente', label: 'Pendente' },
      { id: 'aprovada', label: 'Aprovada' },
      { id: 'rejeitada', label: 'Rejeitada' },
      { id: 'em_andamento', label: 'Em andamento' },
      { id: 'cancelada', label: 'Cancelada' },
    ],
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewingTrip, setViewingTrip] = useState<Trip | null>(null);
  const [finalizeConfirmId, setFinalizeConfirmId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [linkedCheckinConfirm, setLinkedCheckinConfirm] = useState<{ checkinId: number; tripId: number } | null>(null);

  const [vehicleId, setVehicleId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [destination, setDestination] = useState('');
  const [purpose, setPurpose] = useState('');
  const [finalMileage, setFinalMileage] = useState('');
  const [usageDurationMinutes, setUsageDurationMinutes] = useState('');
  const [finalizeCheckoutImage, setFinalizeCheckoutImage] = useState<string | null>(null);

  useEffect(() => {
    setPageTitle('Solicitações de Viagem');
  }, [setPageTitle]);

  const fetchSolicitacoes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!user?.employee_id) {
        setSolicitacoes([]);
        setAvailableVehicles([]);
        setActiveCheckins([]);
        setError('Seu usuário não está vinculado a um funcionário.');
        return;
      }

      const [trips, vehicles, checkins] = await Promise.all([
        tripService.getAll(),
        vehicleService.getAllVehicles(),
        checkinService.getAll().catch(() => []),
      ]);

      setSolicitacoes((trips || []).filter((trip) => trip.employee_id === user!.employee_id));
      setAvailableVehicles(vehicles || []);
      setActiveCheckins((checkins || []).filter((c) => c.status === 'in_progress'));
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao carregar solicitações de viagem.'));
      setSolicitacoes([]);
      setAvailableVehicles([]);
      setActiveCheckins([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void fetchSolicitacoes();
  }, [fetchSolicitacoes]);

  const resetCreateForm = () => {
    setVehicleId(null);
    setStartDate('');
    setEndDate('');
    setPurpose('');
    setDestination('');
  };

  const resetFinalizeForm = () => {
    setFinalizeConfirmId(null);
    setFinalMileage('');
    setUsageDurationMinutes('');
    setFinalizeCheckoutImage(null);
  };

  const openCreateModal = () => {
    resetCreateForm();
    setError(null);
    setIsModalOpen(true);
  };

  const closeCreateModal = () => {
    resetCreateForm();
    setIsModalOpen(false);
  };

  const formatStatus = (status?: string) => {
    switch (status) {
      case 'pendente': return 'Pendente';
      case 'aprovada': return 'Aprovada';
      case 'rejeitada': return 'Rejeitada';
      case 'em_andamento': return 'Em andamento';
      case 'concluida': return 'Concluída';
      case 'cancelada': return 'Cancelada';
      default: return status ?? '-';
    }
  };

  const renderStatusBadge = (status?: string) => {
    switch (status) {
      case 'pendente':
        return (
          <Badge tone="amber" className="gap-1.5">
            <Clock className="h-3 w-3 stroke-[2]" />
            Pendente
          </Badge>
        );
      case 'aprovada':
        return (
          <Badge tone="emerald" className="gap-1.5">
            <CheckCircle2 className="h-3 w-3 stroke-[2]" />
            Aprovada
          </Badge>
        );
      case 'rejeitada':
        return (
          <Badge tone="red" className="gap-1.5">
            <XCircle className="h-3 w-3 stroke-[2]" />
            Rejeitada
          </Badge>
        );
      case 'em_andamento':
        return (
          <Badge tone="blue" className="gap-1.5">
            <Clock className="h-3 w-3 stroke-[2]" />
            Em andamento
          </Badge>
        );
      case 'concluida':
        return (
          <Badge tone="slate" className="gap-1.5">
            <CheckCircle2 className="h-3 w-3 stroke-[2]" />
            Concluída
          </Badge>
        );
      case 'cancelada':
        return (
          <Badge tone="slate" className="gap-1.5">
            <X className="h-3 w-3 stroke-[2]" />
            Cancelada
          </Badge>
        );
      default:
        return formatStatus(status);
    }
  };

  const formatDate = (dateStr?: string) => formatDatePtBR(dateStr);

  const formatTime = (dateStr?: string) => formatTimePtBR(dateStr);

  const formatDateTime = (dateStr?: string) => formatDateTimePtBR(dateStr);

  const formatMinutes = (minutes?: number) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (!hours) return `${remainingMinutes} min`;
    if (!remainingMinutes) return `${hours}h`;
    return `${hours}h ${remainingMinutes}min`;
  };

  const getVehicleDetails = (trip: Trip) =>
    trip.vehicle || availableVehicles.find((vehicle) => vehicle.id === trip.vehicle_id);

  const filteredSolicitacoes = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const statusFilter = appliedFilters.Status || [];
    return solicitacoes
      .filter((s) => s.status !== 'concluida')
      .filter((s) => statusFilter.length === 0 || statusFilter.includes(s.status || ''))
      .filter((s) => {
        const purpose = (s.purpose ?? '').toLowerCase();
        const destination = (s.destination ?? '').toLowerCase();
        return !term || purpose.includes(term) || destination.includes(term);
      });
  }, [solicitacoes, searchTerm, appliedFilters]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredSolicitacoes.length / ITEMS_PER_PAGE)),
    [filteredSolicitacoes.length],
  );

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [filteredSolicitacoes.length, currentPage, totalPages]);

  const { paginatedTrips, pageRange } = useMemo(() => {
    const total = filteredSolicitacoes.length;
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return {
      paginatedTrips: filteredSolicitacoes.slice(startIndex, endIndex),
      pageRange: {
        start: total === 0 ? 0 : startIndex + 1,
        end: Math.min(endIndex, total),
        total,
      },
    };
  }, [filteredSolicitacoes, currentPage, totalPages]);

  const getPageNumbers = useMemo(() => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPages - 1, currentPage + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('...');
    pages.push(totalPages);
    return pages;
  }, [currentPage, totalPages]);

  const handleEnviarSolicitacao = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vehicleId || !startDate || isNaN(Number(vehicleId)) || isNaN(Date.parse(startDate))) {
      setError('Preencha todos os campos obrigatórios corretamente.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (!user?.employee_id) {
      setError('Seu usuário não está vinculado a um funcionário.');
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      await tripService.create({
        vehicle_id: Number(vehicleId),
        employee_id: user.employee_id,
        start_date: new Date(startDate).toISOString(),
        end_date: endDate ? new Date(endDate).toISOString() : undefined,
        purpose: purpose || undefined,
        destination: destination || undefined,
        status: 'pendente',
      });
      await fetchSolicitacoes();
      closeCreateModal();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao enviar solicitação de viagem.'));
    } finally {
      setSubmitting(false);
    }
  };

  const openFinalizeModal = (trip: Trip) => {
    const linkedCheckin = activeCheckins.find(
      (c) => c.vehicle_id === trip.vehicle_id && c.employee_id === trip.employee_id,
    );

    if (linkedCheckin) {
      setLinkedCheckinConfirm({ checkinId: linkedCheckin.id, tripId: trip.id });
      return;
    }

    setFinalizeConfirmId(trip.id);
    setFinalMileage(
      typeof getVehicleDetails(trip)?.mileage === 'number'
        ? String(getVehicleDetails(trip)?.mileage)
        : '',
    );
    setUsageDurationMinutes('');
    setFinalizeCheckoutImage(null);
    setError(null);
  };

  const handleLinkedCheckinConfirmCancel = () => {
    if (!linkedCheckinConfirm) return;
    setLinkedCheckinConfirm(null);
    setFinalizeConfirmId(linkedCheckinConfirm.tripId);
    const trip = solicitacoes.find((t) => t.id === linkedCheckinConfirm.tripId);
    if (trip) {
      setFinalMileage(
        typeof getVehicleDetails(trip)?.mileage === 'number'
          ? String(getVehicleDetails(trip)?.mileage)
          : '',
      );
      setUsageDurationMinutes('');
      setFinalizeCheckoutImage(null);
      setError(null);
    }
  };

  const handleLinkedCheckinConfirmOk = () => {
    if (!linkedCheckinConfirm) return;
    const { checkinId, tripId } = linkedCheckinConfirm;
    setLinkedCheckinConfirm(null);
    navigate('/user/check-in', {
      state: {
        openCheckoutForCheckinId: checkinId,
        preferredTripId: tripId,
      },
    });
  };

  const handleFinalizar = async (id: number) => {
    const parsedFinalMileage = Number(finalMileage);
    const parsedUsageDuration = Number(usageDurationMinutes);

    if (!Number.isInteger(parsedFinalMileage) || parsedFinalMileage < 0) {
      setError('Informe uma quilometragem final válida.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!Number.isInteger(parsedUsageDuration) || parsedUsageDuration < 1) {
      setError('Informe o tempo de uso em minutos.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      const payload: Parameters<typeof tripService.finalize>[1] = {
        final_mileage: parsedFinalMileage,
        usage_duration_minutes: parsedUsageDuration,
      };
      if (finalizeCheckoutImage) {
        const compressed = await resizeImage(finalizeCheckoutImage, 800, 800, 0.7);
        payload.checkoutImage = compressed.replace(/^data:image\/\w+;base64,/, '');
      }
      await tripService.finalize(id, payload);
      await fetchSolicitacoes();
      resetFinalizeForm();
      setSubmitting(false);
      showToast({
        type: 'success',
        title: 'Viagem finalizada com sucesso!',
      });
    } catch (err: unknown) {
      const errorMessage = err instanceof Error
        ? err.message
        : 'Falha ao finalizar a viagem';
      setError(errorMessage);
      showToast({
        type: 'error',
        title: 'Não foi possível finalizar a viagem',
        description: errorMessage,
      });
      setSubmitting(false);
    }
  };

  const handleIniciarViagem = async (trip: Trip) => {
    try {
      const response = await tripService.accept(trip.id);
      await fetchSolicitacoes();
      navigate('/user/check-in', {
        state: {
          trip: response.updatedTrip,
          openModal: true,
        },
      });
    } catch (err: unknown) {
      const errorMessage = err instanceof Error
        ? err.message
        : 'Falha ao iniciar a viagem';
      setError(errorMessage);
      showToast({
        type: 'error',
        title: 'Não foi possível iniciar a viagem',
        description: errorMessage,
      });
    }
  };

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-7">
        <PageHeader title="Solicitações de Viagem" hideTitleInBody />

        <div className="flex flex-col gap-5">
          <div className="mb-2 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[15px] font-semibold leading-tight text-sky-600">Operações — Roteiros e Viagens</p>
              <h1 className="mt-1 text-[22px] sm:text-[26px] font-bold leading-[1.15] tracking-tight text-slate-900">
                Solicitações de viagem.
                <span className="ml-1.5 text-slate-500 font-semibold text-[18px] sm:text-[20px]">
                  Acompanhe aprovações, envie novas e inicie roteiros.
                </span>
              </h1>
              <p className="mt-2 text-[14px] leading-relaxed text-slate-500 max-w-3xl">
                Solicite novos roteiros, acompanhe o status das aprovações do setor, inicie viagens aprovadas e marque como concluídas quando finalizadas.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="secondary" size="lg" onClick={() => void fetchSolicitacoes()} disabled={loading || submitting}>
                <RefreshCw className={`h-[18px] w-[18px] stroke-[2.1] ${loading ? 'animate-spin' : ''}`} />
                Atualizar
              </Button>
              <Button variant="primary" size="lg" onClick={openCreateModal} disabled={loading || submitting}>
                <Plus className="h-[18px] w-[18px] stroke-[2.1]" />
                Nova Solicitação
              </Button>
            </div>
          </div>

          {error && (
            <div className="inline-flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
              <div className="flex flex-col gap-0.5">
                <span className="text-[13px] font-bold leading-tight">Não foi possível prosseguir</span>
                <span className="text-[12.5px] font-medium leading-tight text-rose-700/90">{error}</span>
              </div>
            </div>
          )}

          {loading ? (
            <SectionCard>
              <CardBody>
                <div className="flex items-center justify-center gap-2.5 py-20">
                  <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                  <span className="text-[13.5px] font-semibold text-slate-600">Carregando solicitações de viagem...</span>
                </div>
              </CardBody>
            </SectionCard>
          ) : (
            <SectionCard>
              <CardHeader>
                <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 text-white shadow-[0_6px_16px_-8px_rgba(249,115,22,0.65)]">
                      <ClipboardCheck className="h-[20px] w-[20px] stroke-[1.9]" />
                    </div>
                    <div>
                      <h2 className="text-[15px] font-bold tracking-tight text-slate-900">Suas solicitações em aberto</h2>
                      <p className="mt-0.5 text-[12.5px] font-medium leading-tight text-slate-500">
                        Pendentes, aprovadas, rejeitadas, em andamento ou canceladas
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <div className="relative flex-1 sm:w-[360px] shrink-0">
                      <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <Input
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                        placeholder="Pesquisar destino ou propósito..."
                        className="!pl-10 h-10"
                      />
                    </div>
                    <Button
                      variant="secondary"
                      size="md"
                      onClick={() => setIsFilterOpen(true)}
                      className="h-10"
                    >
                      <Filter className="h-[17px] w-[17px] stroke-[1.9]" />
                      Filtros
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                <ModernTable className="table-fixed layout-fixed">
                  <Thead>
                    <Tr>
                      <Th className="w-[14%] min-w-[150px] max-w-[190px]">Destino</Th>
                      <Th className="w-[10%] min-w-[110px] max-w-[150px]">Data / Hora</Th>
                      <Th className="w-[18%] min-w-[200px] max-w-[280px]">Motivo</Th>
                      <Th className="w-[15%] min-w-[160px] max-w-[220px]">Veículo</Th>
                      <Th className="w-[15%] min-w-[160px] max-w-[220px]">Funcionário</Th>
                      <Th className="w-[11%] min-w-[120px] max-w-[160px]">Status</Th>
                      <Th className="w-[14%] min-w-[160px] max-w-[200px]">Ações</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {paginatedTrips.length === 0 ? (
                      <Tr>
                        <Td colSpan={7}>
                          <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
                            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                              <ClipboardCheck className="h-7 w-7 stroke-[1.8]" />
                            </div>
                            <div className="text-[14px] font-bold text-slate-700">
                              {searchTerm || (appliedFilters.Status || []).length > 0
                                ? 'Nenhuma solicitação encontrada'
                                : 'Nenhuma solicitação em aberto'}
                            </div>
                            <p className="max-w-sm text-[12.5px] font-medium leading-relaxed text-slate-500">
                              {searchTerm || (appliedFilters.Status || []).length > 0
                                ? 'Nenhum resultado para a busca e filtros atuais.'
                                : 'Clique em "Nova Solicitação" para enviar uma nova solicitação de viagem.'}
                            </p>
                          </div>
                        </Td>
                      </Tr>
                    ) : (
                      paginatedTrips.map((s) => {
                        const veiculo = getVehicleDetails(s);
                        return (
                          <Tr key={s.id}>
                            <Td>
                              <div className="min-w-0 w-full max-w-[190px] overflow-hidden">
                                <div className="flex items-start gap-2">
                                  <MapPin className="mt-[3px] h-3.5 w-3.5 shrink-0 text-slate-400 stroke-[1.8]" />
                                  <span
                                    className="block truncate text-[13.5px] font-semibold leading-tight text-slate-900"
                                    title={s.destination || '-'}
                                  >
                                    {s.destination || '-'}
                                  </span>
                                </div>
                              </div>
                            </Td>
                            <Td>
                              <div className="inline-flex flex-col gap-0.5">
                                <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-[13px] font-semibold text-slate-900 tabular-nums">
                                  <CalendarDays className="h-[15px] w-[15px] text-slate-400 stroke-[1.8]" />
                                  {formatDate(s.start_date)}
                                </span>
                                <span className="ml-[26px] text-[11.5px] font-medium leading-tight text-slate-500 tabular-nums">
                                  {formatTime(s.start_date)}
                                </span>
                              </div>
                            </Td>
                            <Td>
                              <div className="min-w-0 w-full max-w-[280px] overflow-hidden">
                                <div className="flex items-start gap-2">
                                  <FileText className="mt-[3px] h-3.5 w-3.5 shrink-0 text-slate-400 stroke-[1.8]" />
                                  <span
                                    className="block truncate text-[13px] font-medium leading-tight text-slate-700"
                                    title={s.purpose || '-'}
                                  >
                                    {s.purpose || '-'}
                                  </span>
                                </div>
                              </div>
                            </Td>
                            <Td>
                              <div className="min-w-0 w-full max-w-[220px] overflow-hidden">
                                <div className="flex items-start gap-2.5">
                                  <div className="mt-0.5 shrink-0 flex h-9 w-9 min-w-[36px] items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
                                    <Car className="h-[18px] w-[18px] stroke-[1.9]" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <span
                                      className="block max-w-[180px] truncate text-[13.5px] font-semibold leading-tight text-slate-900"
                                      title={veiculo?.brand || '-'}
                                    >
                                      {veiculo?.brand || '-'}
                                    </span>
                                    <span className="block max-w-[180px] truncate mt-0.5 text-[11.5px] font-medium leading-tight text-slate-500">
                                      Placa: {veiculo?.license_plate || '-'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </Td>
                            <Td>
                              <div className="min-w-0 w-full max-w-[220px] overflow-hidden">
                                <div className="flex items-start gap-2.5">
                                  <div className="mt-0.5 shrink-0 flex h-9 w-9 min-w-[36px] items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200">
                                    <User className="h-[18px] w-[18px] stroke-[1.9]" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <span
                                      className="block max-w-[180px] truncate text-[13.5px] font-semibold leading-tight text-slate-900"
                                      title={s.employee?.name || '-'}
                                    >
                                      {s.employee?.name || '-'}
                                    </span>
                                    <span className="block max-w-[180px] truncate mt-0.5 text-[11.5px] font-medium leading-tight text-slate-500">
                                      {s.employee?.cpf || s.employee_id || '-'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </Td>
                            <Td>{renderStatusBadge(s.status)}</Td>
                            <Td>
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => setViewingTrip(s)}
                                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                                  title="Visualizar detalhes"
                                >
                                  <Eye className="h-[17px] w-[17px] stroke-[1.9]" />
                                </button>
                                {s.status === 'aprovada' && user?.role === 'user' && (
                                  <button
                                    type="button"
                                    onClick={() => handleIniciarViagem(s)}
                                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 transition-all duration-200 hover:border-emerald-300 hover:bg-emerald-100 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:ring-offset-1"
                                    title="Iniciar viagem"
                                  >
                                    <Play className="h-[16px] w-[16px] stroke-[2.1] fill-emerald-600/20" />
                                  </button>
                                )}
                                {s.status === 'em_andamento' && user?.role === 'user' && (
                                  <button
                                    type="button"
                                    onClick={() => openFinalizeModal(s)}
                                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-700 transition-all duration-200 hover:border-blue-300 hover:bg-blue-100 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                                    title="Finalizar viagem"
                                  >
                                    <Flag className="h-[17px] w-[17px] stroke-[1.9]" />
                                  </button>
                                )}
                              </div>
                            </Td>
                          </Tr>
                        );
                      })
                    )}
                  </Tbody>
                </ModernTable>

                {pageRange.total > 0 ? (
                  <div className="mt-4 flex flex-col gap-3 border-t border-slate-200/80 pt-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-[12.5px] font-medium leading-tight text-slate-500">
                      Mostrando{' '}
                      <span className="font-bold tabular-nums text-slate-800">{pageRange.start}</span>
                      {'–'}{' '}
                      <span className="font-bold tabular-nums text-slate-800">{pageRange.end}</span>
                      {' de '}
                      <span className="font-bold tabular-nums text-slate-800">{pageRange.total}</span>
                      {' solicitação(ões)'}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                        aria-label="Primeira página"
                      >
                        <ChevronsLeft className="h-4 w-4 stroke-[1.8]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                        aria-label="Página anterior"
                      >
                        <ChevronLeft className="h-4 w-4 stroke-[1.8]" />
                      </button>
                      <div className="inline-flex items-center gap-1 mx-1">
                        {getPageNumbers.map((n, idx) =>
                          n === '...' ? (
                            <span key={`dots-${idx}`} className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 text-[13px]">…</span>
                          ) : (
                            <button
                              key={n}
                              type="button"
                              onClick={() => setCurrentPage(n)}
                              disabled={submitting}
                              className={`inline-flex h-9 min-w-[36px] cursor-pointer items-center justify-center rounded-lg px-2.5 text-[13px] font-semibold transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1 ${
                                n === currentPage
                                  ? 'bg-blue-600 text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)]'
                                  : 'border border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                              }`}
                            >
                              {n}
                            </button>
                          ),
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                        aria-label="Próxima página"
                      >
                        <ChevronRight className="h-4 w-4 stroke-[1.8]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                        aria-label="Última página"
                      >
                        <ChevronsRight className="h-4 w-4 stroke-[1.8]" />
                      </button>
                    </div>
                  </div>
                ) : null}
              </CardBody>
            </SectionCard>
          )}
        </div>

        {isModalOpen && (
          <Modal
            onClose={closeCreateModal}
            maxWidthClass="max-w-2xl"
            title={
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 text-white shadow-[0_6px_16px_-8px_rgba(249,115,22,0.65)]">
                  <Plus className="h-5 w-5 stroke-[2]" />
                </div>
                <div>
                  <div className="text-[16px] font-bold tracking-tight text-slate-900 leading-none">Nova Solicitação de Viagem</div>
                  <div className="mt-1 text-[12px] font-medium text-slate-500 leading-tight">
                    Preencha os dados abaixo para solicitar uma nova viagem
                  </div>
                </div>
              </div>
            }
            footer={
              <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:items-center">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={closeCreateModal}
                  disabled={submitting}
                  className="h-10 px-5"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  form="nova-solicitacao-form"
                  variant="primary"
                  isLoading={submitting}
                  loadingLabel="Enviando..."
                  className="h-10 px-5"
                >
                  <Plus className="h-[17px] w-[17px] stroke-[1.9]" />
                  Enviar Solicitação
                </Button>
              </div>
            }
          >
            <form id="nova-solicitacao-form" onSubmit={handleEnviarSolicitacao} className="space-y-5" noValidate>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="destino">
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" />
                      Destino
                    </span>
                    <span className="ml-0.5 text-rose-500 font-bold leading-none">*</span>
                  </Label>
                  <Input
                    id="destino"
                    type="text"
                    value={destination}
                    onChange={(e) => setDestination(e.target.value)}
                    required
                    placeholder="Ex: Hospital Municipal"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="dataHora">
                    <span className="inline-flex items-center gap-1">
                      <CalendarDays className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" />
                      Data e Hora (Início)
                    </span>
                    <span className="ml-0.5 text-rose-500 font-bold leading-none">*</span>
                  </Label>
                  <Input
                    id="dataHora"
                    type="datetime-local"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="motivo">
                  <span className="inline-flex items-center gap-1">
                    <FileText className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" />
                    Motivo / Propósito
                  </span>
                  <span className="ml-0.5 text-rose-500 font-bold leading-none">*</span>
                </Label>
                <textarea
                  id="motivo"
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                  rows={3}
                  required
                  className="w-full resize-y rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-[13.5px] leading-relaxed text-slate-900 transition focus:border-blue-400 focus:outline-none focus:ring-4 focus:ring-blue-500/10"
                  placeholder="Descreva o motivo da solicitação, incluindo objetivos e detalhes relevantes..."
                />
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="veiculo">
                    <span className="inline-flex items-center gap-1">
                      <Car className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" />
                      Veículo
                    </span>
                    <span className="ml-0.5 text-rose-500 font-bold leading-none">*</span>
                  </Label>
                  <Select
                    id="veiculo"
                    value={vehicleId ?? ''}
                    onChange={(e) => setVehicleId(e.target.value ? Number(e.target.value) : null)}
                  >
                    <option value="">Selecione um veículo...</option>
                    {availableVehicles.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.brand || 'Veículo'} - {v.license_plate || 'sem placa'}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>
                    <span className="inline-flex items-center gap-1">
                      <User className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" />
                      Funcionário (solicitante)
                    </span>
                  </Label>
                  <div className="flex h-10 items-center rounded-lg border border-slate-200 bg-slate-50 px-3.5 text-[13.5px] font-semibold text-slate-800">
                    {user?.name || 'Usuário logado'}
                  </div>
                </div>
              </div>
            </form>
          </Modal>
        )}

        {viewingTrip && (
          <Modal
            onClose={() => setViewingTrip(null)}
            maxWidthClass="max-w-3xl"
            title={
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 via-blue-500 to-sky-600 text-white shadow-[0_6px_16px_-8px_rgba(79,70,229,0.6)]">
                  <Eye className="h-5 w-5 stroke-[1.9]" />
                </div>
                <div>
                  <div className="text-[16px] font-bold tracking-tight text-slate-900 leading-none">
                    Detalhes da Solicitação #{viewingTrip.id}
                  </div>
                  <div className="mt-1 text-[12px] font-medium text-slate-500 leading-tight">
                    Acompanhe todas as informações desta solicitação
                  </div>
                </div>
                <div className="ml-auto">{renderStatusBadge(viewingTrip.status)}</div>
              </div>
            }
            footer={
              <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:items-center">
                <Button variant="secondary" onClick={() => setViewingTrip(null)} className="h-10 px-5">
                  Voltar
                </Button>
                {viewingTrip.status === 'aprovada' && user?.role === 'user' && (
                  <Button
                    variant="primary"
                    onClick={() => handleIniciarViagem(viewingTrip)}
                    className="h-10 px-5"
                  >
                    <Play className="h-[17px] w-[17px] stroke-[2]" />
                    Iniciar Viagem
                  </Button>
                )}
                {viewingTrip.status === 'em_andamento' && user?.role === 'user' && (
                  <Button
                    variant="primary"
                    onClick={() => {
                      setViewingTrip(null);
                      openFinalizeModal(viewingTrip);
                    }}
                    className="h-10 px-5"
                  >
                    <Flag className="h-[17px] w-[17px] stroke-[1.9]" />
                    Marcar como Concluída
                  </Button>
                )}
              </div>
            }
          >
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Destino</div>
                  <div className="mt-1 flex items-start gap-2 text-sm text-slate-900 font-semibold">
                    <MapPin className="mt-[2px] h-3.5 w-3.5 shrink-0 stroke-[1.8] text-slate-400" />
                    <span className="truncate" title={viewingTrip.destination || '-'}>
                      {viewingTrip.destination || '-'}
                    </span>
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Data e Hora</div>
                  <div className="mt-1 flex items-center gap-2 text-sm text-slate-900 font-semibold tabular-nums">
                    <CalendarDays className="h-3.5 w-3.5 shrink-0 stroke-[1.8] text-slate-400" />
                    {formatDateTime(viewingTrip.start_date)}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 md:col-span-2">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Motivo</div>
                  <div className="mt-1 flex items-start gap-2 text-sm text-slate-900 font-semibold leading-relaxed whitespace-pre-wrap break-words">
                    <FileText className="mt-[2px] h-3.5 w-3.5 shrink-0 stroke-[1.8] text-slate-400" />
                    <span>{viewingTrip.purpose || '-'}</span>
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Veículo</div>
                  <div className="mt-1 space-y-0.5">
                    <div className="text-sm text-slate-900 font-semibold">
                      {getVehicleDetails(viewingTrip)?.brand || '-'}
                    </div>
                    <div className="text-[11.5px] font-medium text-slate-500">
                      Placa: {getVehicleDetails(viewingTrip)?.license_plate || '-'}
                    </div>
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Status</div>
                  <div className="mt-1">{renderStatusBadge(viewingTrip.status)}</div>
                </div>
                {typeof viewingTrip.final_mileage === 'number' && (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Quilometragem Final</div>
                    <div className="mt-1 flex items-center gap-2 text-sm text-slate-900 font-semibold tabular-nums">
                      <Gauge className="h-3.5 w-3.5 shrink-0 stroke-[1.8] text-indigo-600" />
                      {viewingTrip.final_mileage.toLocaleString('pt-BR')} km
                    </div>
                  </div>
                )}
                {typeof viewingTrip.usage_duration_minutes === 'number' && (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Tempo de uso</div>
                    <div className="mt-1 flex items-center gap-2 text-sm text-slate-900 font-semibold tabular-nums">
                      <Timer className="h-3.5 w-3.5 shrink-0 stroke-[1.8] text-slate-400" />
                      {formatMinutes(viewingTrip.usage_duration_minutes)}
                    </div>
                  </div>
                )}
                {viewingTrip.status === 'cancelada' && viewingTrip.cancellation_reason && (
                  <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 md:col-span-2">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-rose-500">Motivo do Cancelamento</div>
                    <div className="mt-1 flex items-start gap-2 text-sm text-rose-800 font-semibold leading-relaxed whitespace-pre-wrap break-words">
                      <AlertCircle className="mt-[2px] h-3.5 w-3.5 shrink-0 stroke-[1.9] text-rose-600" />
                      <span>{viewingTrip.cancellation_reason}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Modal>
        )}

        {finalizeConfirmId !== null && (
          <Modal
            onClose={resetFinalizeForm}
            maxWidthClass="max-w-xl"
            title={
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 via-teal-500 to-emerald-600 text-white shadow-[0_6px_16px_-8px_rgba(16,185,129,0.6)]">
                  <Flag className="h-5 w-5 stroke-[1.9]" />
                </div>
                <div>
                  <div className="text-[16px] font-bold tracking-tight text-slate-900 leading-none">Finalizar Viagem</div>
                  <div className="mt-1 text-[12px] font-medium text-slate-500 leading-tight">
                    Informe os dados de encerramento para concluir o roteiro
                  </div>
                </div>
              </div>
            }
            footer={
              <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:items-center">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={resetFinalizeForm}
                  disabled={submitting}
                  className="h-10 px-5"
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => finalizeConfirmId !== null && handleFinalizar(finalizeConfirmId)}
                  isLoading={submitting}
                  loadingLabel="Salvando..."
                  className="h-10 px-5"
                >
                  <CheckCircle2 className="h-[17px] w-[17px] stroke-[1.9]" />
                  Confirmar
                </Button>
              </div>
            }
          >
            <div className="space-y-4">
              <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-emerald-800">
                <div className="flex items-start gap-2">
                  <AlertCircle className="mt-[1px] h-[18px] w-[18px] shrink-0 stroke-[1.9]" />
                  <div>
                    <p className="font-semibold text-[13px] leading-tight">Confirmar encerramento</p>
                    <p className="mt-0.5 text-[12.5px] font-medium text-emerald-800/90 leading-relaxed">
                      Após confirmação, a viagem será marcada como concluída e não poderá ser editada novamente.
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="final-mileage">
                  <span className="inline-flex items-center gap-1">
                    <Gauge className="h-3.5 w-3.5 stroke-[1.9] text-indigo-600" />
                    Quilometragem Final (km)
                  </span>
                  <span className="ml-0.5 text-rose-500 font-bold leading-none">*</span>
                </Label>
                <Input
                  id="final-mileage"
                  type="number"
                  min="0"
                  step="1"
                  value={finalMileage}
                  onChange={(e) => setFinalMileage(e.target.value)}
                  placeholder="Ex: 15420"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="usage-duration">
                  <span className="inline-flex items-center gap-1">
                    <Timer className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" />
                    Tempo de Uso (minutos)
                  </span>
                  <span className="ml-0.5 text-rose-500 font-bold leading-none">*</span>
                </Label>
                <Input
                  id="usage-duration"
                  type="number"
                  min="1"
                  step="1"
                  value={usageDurationMinutes}
                  onChange={(e) => setUsageDurationMinutes(e.target.value)}
                  placeholder="Ex: 90"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="final-checkout-photo">
                  <span className="inline-flex items-center gap-1">
                    <Camera className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" />
                    Foto do Check-Out (painel/combustível)
                  </span>
                </Label>
                <div className="rounded-lg border border-dashed border-slate-300 p-4 text-center bg-slate-50/60">
                  {finalizeCheckoutImage ? (
                    <div className="space-y-3">
                      <img
                        src={finalizeCheckoutImage}
                        alt="Check-out preview"
                        className="mx-auto max-h-48 rounded-lg shadow-sm object-contain"
                      />
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => setFinalizeCheckoutImage(null)}
                        className="h-9"
                      >
                        <Trash2 className="h-4 w-4 stroke-[1.9] text-rose-600" />
                        Remover foto
                      </Button>
                    </div>
                  ) : (
                    <label className="inline-flex cursor-pointer flex-col items-center gap-1 text-slate-500 hover:text-blue-700">
                      <Camera className="text-2xl stroke-[1.8]" />
                      <span className="text-sm font-medium">Clique para enviar foto</span>
                      <span className="text-[11.5px] font-medium text-slate-400">
                        JPG, PNG ou WebM até 8 MB
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        id="final-checkout-photo"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = (ev) =>
                              setFinalizeCheckoutImage((ev.target?.result as string) || null);
                            reader.readAsDataURL(file);
                          }
                          e.target.value = '';
                        }}
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>
          </Modal>
        )}

        <ConfirmModal
          isOpen={linkedCheckinConfirm !== null}
          title="Check-in em andamento encontrado"
          confirmVariant="primary"
          confirmLabel="Ir para Check-In"
          cancelLabel="Usar formulário de finalização"
          onConfirm={handleLinkedCheckinConfirmOk}
          onCancel={handleLinkedCheckinConfirmCancel}
          description={
            <div className="space-y-2.5 text-[13.5px] text-slate-600 leading-relaxed">
              <p>Para sua conveniência, esta viagem já tem um check-in <strong>em andamento ativo</strong> vinculado ao mesmo veículo e motorista.</p>
              <p>Clique em <strong>Ir para Check-In</strong> para finalizar tudo em uma única tela: foto do check-out + quilometragem final, evitando digitar os dados duas vezes.</p>
              <p>Clique em <strong>Usar formulário de finalização</strong> se preferir seguir com a finalização direta da viagem nesta página.</p>
            </div>
          }
        />

        <FilterOptions
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApplyFilter={(selectedFilters) => {
            setAppliedFilters(selectedFilters);
            setCurrentPage(1);
          }}
          filterCategories={FILTER_CATEGORIES}
          initialFilters={appliedFilters}
        />
      </div>
    </div>
  );
};

export default SolicitacaoViagem;
